import { useTranslation } from 'react-i18next';

import { Box, Button, Flex, forwardRef, rem, Text, VStack } from '@bonusly/design-system';

import { useActionInput } from '../../context';
import { Budget } from '../../lib/entities/budget';
import { SelectorBox, SelectorProps } from '../selector/selector-box';
import { useSelector } from '../selector/use-selector';
import { AmountBalance } from './amount-balance';
import { FlashPointsCountdown } from './flash-points-countdown';

interface AmountSelectorProps extends SelectorProps<number> {
	allowanceBoostId?: string;
	budget: Budget;
}

export const AmountSelector = forwardRef<AmountSelectorProps, 'div'>(({ allowanceBoostId, budget, command, hash, items }, ref) => {
	const { t } = useTranslation('components');
	const { context } = useActionInput();
	const { getOptionProps, getSelectorProps, selectedIndex } = useSelector({
		command,
		hash,
		items,
		ref,
	});

	const isAwardContext = context.includes('award');

	if (!items.length) {
		return null;
	}

	return (
		<SelectorBox p={0}>
			{!isAwardContext && <FlashPointsCountdown />}
			<VStack p={3}>
				<Box overflowX="auto">
					<Flex flexWrap={{ base: 'wrap', xs: 'nowrap' }} gap={3} justifyContent={{ base: 'center', xs: 'unset' }} {...getSelectorProps()}>
						{items.map((amount, index) => (
							<Button
								aria-label={t('rich_media.amount.select_amount', { amount })}
								key={amount}
								backgroundColor={index === selectedIndex ? 'green.50' : 'green.25'}
								borderRadius={rem(60)}
								color="green.700"
								h={8}
								px={2}
								role="option"
								_hover={{
									backgroundColor: 'green.50',
									color: 'green.700',
								}}
								{...getOptionProps(index)}
							>
								<Text mr="1px" size="xl" fontWeight="medium">
									+
								</Text>
								<Text size="xl" fontWeight="medium">
									{amount}
								</Text>
							</Button>
						))}
					</Flex>
				</Box>

				{budget.hasAllowance && <AmountBalance allowanceBoostId={allowanceBoostId} budget={budget} />}
			</VStack>
		</SelectorBox>
	);
});
